import styled from '@emotion/styled';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';

import { Button } from '@travelnest/hui';

import { Stack } from '@/components/ui/Stack';
import { colors } from '@/lib/theme';
import { useI18n } from '@/locales';

import { MoreAction } from './MoreAction';

const Clamp = styled.div<{ expanded: boolean; maxHeight: number }>`
	position: relative;
	overflow: hidden;
	max-height: ${({ expanded, maxHeight }) => (expanded ? 'none' : `${maxHeight}px`)};

	&::after {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		height: 56px;
		background: linear-gradient(
			to bottom,
			rgba(255, 255, 255, 0),
			${colors.LM_NU0}
		);
		opacity: ${({ expanded }) => (expanded ? 0 : 1)};
		pointer-events: none;
		transition: opacity 200ms ease-out;
	}
`;

interface ExpandableProps {
	children: ReactNode;
	/** Height in px to show before "Show more". */
	collapsedHeight?: number;
	/** Custom label for the expand button, e.g. "Show all 24 amenities". */
	expandLabel?: string;
	/** Show the whole content elsewhere (e.g. a dialog) instead of unfolding it here. */
	onShowMore?: () => void;
	className?: string;
}

/**
 * Collapses long content behind a "Show more" button. The button only appears
 * when the content really is taller than the collapsed height.
 */
export const Expandable = ({
	children,
	collapsedHeight = 160,
	expandLabel,
	onShowMore,
	className,
}: ExpandableProps) => {
	const t = useI18n();
	const id = useId();
	const ref = useRef<HTMLDivElement>(null);
	const [expanded, setExpanded] = useState(false);
	const [overflows, setOverflows] = useState(false);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		const measure = () =>
			setOverflows(el.scrollHeight > collapsedHeight + 8);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, [collapsedHeight, children]);

	const needsToggle = overflows || expanded;

	return (
		<Stack gap="space40" className={className}>
			<Clamp
				id={id}
				ref={ref}
				expanded={expanded || !overflows}
				maxHeight={collapsedHeight}
			>
				{children}
			</Clamp>
			{needsToggle && (
				<MoreAction>
					<Button
						text={
							expanded
								? t('common.showLess')
								: (expandLabel ?? t('common.showMore'))
						}
						variant="secondary"
						fill="light"
						size="regular"
						aria-expanded={onShowMore ? undefined : expanded}
						aria-controls={onShowMore ? undefined : id}
						onClick={onShowMore ?? (() => setExpanded((v) => !v))}
					/>
				</MoreAction>
			)}
		</Stack>
	);
};
